import React from "react";
import { connect } from "react-redux";
import { compose, bindActionCreators } from "redux";
import { withStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import LockIcon from "@material-ui/icons/Lock";

import {
  getCostString,
  getTimeDiff,
  getTimeStr,
  getTimeWithHourStr,
} from "../../../helpers";
import Status from "../../../helpers/Status";

import {
  getRowsAndTotal,
  setReRender,
  setFilter,
} from "../../../../../state/order/actions";
import {
  getListOrigens,
  getLabelOrigem,
  ORIGEM,
} from "../../../../../main/origem";
import { StorageUser } from "../../../../../main/StorageUser";
import { sendGaEvent } from "../../../../../main/sendGaEvent";
import consts from "../../../../../main/consts";
import { salesChannelIsAmericanas } from "../../../../../utils/check-sales-channel";

import FilterBar from "../../components/FilterBar/FilterBar";
import OrdersTable from "../../components/OrdersTable/OrdersTable";
import EmptyState from "../../components/EmptyState/EmptyState";
import TabAlert from "../../components/TabAlert/TabAlert";
import AnalyzeCollectionDialog from "../../components/AnalyzeCollectionDialog/AnalyzeCollectionDialog";
import CancelOrderDialog from "../../components/CancelOrderDialog/CancelOrderDialog";
import styles from "./OrdersTab.styles";

const PAGE_NAME = "ready-to-go";
const ROWS_PER_PAGE = consts.ROWS_PER_PAGE;
const SEARCH_DEBOUNCE = 500;

const CANAL_OPTIONS = [
  { label: "Americanas", value: "VTEX" },
  { label: "iFood", value: "IFOOD" },
  { label: "Magalu", value: "MAGALU" },
  { label: "Shopee", value: "SHOPEE" },
];

const ORIGEM_OPTIONS = getListOrigens().filter(
  (option) => option.value !== "" && option.value !== ORIGEM.PEGUE_NA_LOJA
);

const storageUser = new StorageUser();

class OrdersTab extends React.Component {
  state = {
    update: false,
    modalOpen: false,
    pedido: {},
    search: "",
    canal: "",
    analyzeOpen: false,
    analyzePedido: null,
  };

  canInlineCancel =
    storageUser.allowedByPermission("INLINE_CANCEL") ||
    storageUser.allowedByPermission("ADMIN_INLINE_CANCEL");

  pageViewSent = false;

  componentDidMount() {
    this.loadGridData(10, ROWS_PER_PAGE);
  }

  componentDidUpdate(prevProps) {
    const {
      loja,
      orders: { filters },
      limit,
    } = this.props;

    // A aba monta um OrdersTab novo a cada troca, então o primeiro loaded
    // false -> true marca a entrada do usuário nela, já com os dados na mão.
    if (!prevProps.orders.loaded && this.props.orders.loaded) {
      this.sendPageView();
    }

    if (
      loja !== prevProps.loja ||
      filters !== prevProps.orders.filters ||
      prevProps.limit !== limit
    ) {
      this.loadGridData(10, ROWS_PER_PAGE, true);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
  }

  sendPageView = () => {
    const { gaTab, gaCountKey, orders } = this.props;

    if (this.pageViewSent || !gaTab) {
      return;
    }

    this.pageViewSent = true;

    const grid = orders.grids[PAGE_NAME] || {};

    sendGaEvent("tab_view", PAGE_NAME, {
      tab: gaTab,
      // As abas com contador têm o número exato vindo do back; a Geral só tem
      // o total da grid, que é inferido quando o count vem zerado.
      order_count: gaCountKey ? (grid.counts || {})[gaCountKey] : grid.total,
    });
  };

  loadGridData = (page, limits, avoidSpinner = false) => {
    const {
      getRowsAndTotal,
      orders: { filters },
      limit,
      aba,
    } = this.props;
    const { search, canal } = this.state;

    limits = limits || ROWS_PER_PAGE;

    const find = search.trim();

    getRowsAndTotal({
      page,
      limit,
      filters: {
        ...filters,
        status: "PRONTO_PARA_SAIR",
        ...(aba ? { aba } : {}),
        ...(find ? { find } : {}),
        ...(canal ? { canalVenda: canal } : {}),
      },
      avoidSpinner,
      pageId: PAGE_NAME,
      inferTotal: true,
    });
  };

  reloadFromFilters = () => this.loadGridData(0, ROWS_PER_PAGE, true);

  handleSearchChange = (value) => {
    this.setState({ search: value });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(this.reloadFromFilters, SEARCH_DEBOUNCE);
  };

  handleCanalChange = (value) =>
    this.setState({ canal: value }, this.reloadFromFilters);

  clickRow = (row, event) => {
    !event.defaultPrevented &&
      this.props.history.push(`${consts.CONTEXT}/ready-to-go/${row.numero}`, {
        data: { page: PAGE_NAME },
      });
  };

  handleOrigemChange = (value) => this.props.setFilter("origem", value);

  handleAnalyze = (pedido) =>
    this.setState({ analyzeOpen: true, analyzePedido: pedido });

  handleAnalyzeClose = () =>
    this.setState({ analyzeOpen: false, analyzePedido: null });

  handleAnalyzeConfirm = (option) => {
    const { onAnalyzeConfirm } = this.props;

    onAnalyzeConfirm && onAnalyzeConfirm(option, this.state.analyzePedido);

    this.loadGridData(0, ROWS_PER_PAGE, true);
  };

  setRowState = (data) => this.setState(data);

  hasActiveFilters = () =>
    this.state.search.trim() !== "" ||
    this.state.canal !== "" ||
    !!this.props.orders.filters.origem;

  getHeaders = () => {
    const { classes, renderActions } = this.props;

    const headers = [
      {
        title: "Nº Pedido",
        align: "left",
        dataField: "codigoExterno",
        class: classes.maxWidthCol,
        render: (codigoExterno) => codigoExterno.split("#")[0],
      },
      {
        title: "Data",
        align: "left",
        dataField: "dataInclusao",
        render: (data) => new Date(data).toLocaleDateString(),
      },
      {
        title: "Entrega estimada",
        align: "left",
        dataField: "dhEntregaEstimada",
        render: (data, row) =>
          salesChannelIsAmericanas(row.canalDeVendas)
            ? getTimeStr(data)
            : getTimeWithHourStr(data),
      },
      {
        title: "Tempo",
        align: "left",
        dataField: "dataInclusao",
        class: classes.duracao,
        render: (data) => (
          <Tooltip title="Tempo desde a chegada do pedido">
            <span>{getTimeDiff(data)}</span>
          </Tooltip>
        ),
      },
      {
        title: "Cliente",
        align: "left",
        dataField: "cliente",
        class: classes.capitalize,
        render: (data) => data && data.nome.toLowerCase(),
      },
      {
        title: "Canal de venda",
        align: "left",
        dataField: "canalDeVendas",
        render: (data) => data,
      },
      {
        title: "Origem",
        align: "left",
        dataField: "origem",
        render: (data) => getLabelOrigem(data),
      },
      {
        title: "Status",
        align: "left",
        dataField: "pendencia",
        render: (data) => (
          <Status status={data && data !== "" ? data : "Sem pendência"} />
        ),
      },
      {
        title: "Total",
        align: "right",
        dataField: "valorTotal",
        class: classes.totalCost,
        render: (val, { origem }) =>
          origem === "PEGUE_NA_LOJA" ? (
            <LockIcon style={{ color: "#0000008a" }} />
          ) : (
            getCostString(val)
          ),
      },
    ];

    if (renderActions) {
      headers.push({
        title: "Ações",
        align: "center",
        render: (data, pedido) =>
          renderActions(pedido, {
            onAnalyze: this.handleAnalyze,
            setState: this.setRowState,
            canInlineCancel: this.canInlineCancel,
          }),
      });
    }

    return headers;
  };

  render() {
    if (this.state.update) {
      this.setState({ update: false }, () => {
        this.loadGridData(0, ROWS_PER_PAGE, true);
      });
    }

    const { alert } = this.props;
    const grid = this.props.orders.grids[PAGE_NAME];
    const rows = grid.rows;
    const isTabEmpty =
      this.props.orders.loaded && rows.length === 0 && !this.hasActiveFilters();
    const total = grid.total;

    return (
      <>
        <CancelOrderDialog
          {...this.state.pedido}
          open={this.state.modalOpen}
          setState={this.setRowState}
        />

        {!isTabEmpty && alert && (
          <TabAlert severity={alert.severity} message={alert.message} />
        )}

        {!isTabEmpty && (
          <FilterBar
            search={this.state.search}
            onSearchChange={this.handleSearchChange}
            canal={this.state.canal}
            canalOptions={CANAL_OPTIONS}
            onCanalChange={this.handleCanalChange}
            origem={this.props.orders.filters.origem || ""}
            origemOptions={ORIGEM_OPTIONS}
            onOrigemChange={this.handleOrigemChange}
          />
        )}

        <OrdersTable
          loaded={this.props.orders.loaded}
          limit={this.props.limit}
          page={PAGE_NAME}
          headers={this.getHeaders()}
          onClickRow={this.clickRow}
          rows={rows}
          total={total}
          selectedPage={this.props.orders.page}
          rowsPerPage={ROWS_PER_PAGE}
          onHandlePageChange={this.loadGridData}
          selectedOrigemFilter={getLabelOrigem(grid.selectedFilter)}
          hideHeadersWhenEmpty={isTabEmpty}
          emptyState={
            <EmptyState variant={isTabEmpty ? "no-orders" : "no-results"} />
          }
        />

        <AnalyzeCollectionDialog
          open={this.state.analyzeOpen}
          pedido={this.state.analyzePedido}
          onClose={this.handleAnalyzeClose}
          onConfirm={this.handleAnalyzeConfirm}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  orders: state.orders,
  loja: state.auth.loja,
  limit: state.orders.limit,
  reRender: state.orders.reRender,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getRowsAndTotal, setReRender, setFilter }, dispatch);

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(OrdersTab);
